import axios from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';
import { API_URL } from '../config/api';


if (!API_URL) {
  throw new Error('API_URL is not defined! Check your environment configuration.');
}

export interface HelpRequest {
  _id: string;
  user: {
    _id: string;
    displayName: string;
  };
  title: string;
  description: string;
  category: string;
  status: 'open' | 'resolved';
  createdAt: string;
  updatedAt: string;
}

export type CreateHelpRequestData = {
  title: string;
  description: string;
  category: HelpRequest['category'];
};

export interface Response {
  _id: string;
  content: string;
  helpRequest: string;
  user: {
    _id: string;
    id?: string;
    displayName: string;
  };
  createdAt: string;
  updatedAt: string;
}

class HelpService {
  async getHelpRequests(tab: 'all' | 'pending' | 'recent' | 'answered' | 'offered', userOnly: boolean = true): Promise<HelpRequest[]> {
    try {
      const token = await AsyncStorage.getItem('userToken');
      if (!token) throw new Error('No authentication token found');

      let endpoint = '/help/requests';
      if (tab === 'pending') {
        endpoint = '/help/requests/unanswered';
      } else if (tab === 'recent') {
        endpoint += `?status=resolved${userOnly ? '&userOnly=true' : ''}`;
      } else if (tab === 'answered') {
        endpoint = '/help/requests/answered';
      } else if (tab === 'offered') {
        // For offered tab, we'll fetch responses by the current user
        return this.getUserResponses();
      }

      const response = await axios.get(`${API_URL}${endpoint}`, {
        headers: { 'x-auth-token': token }
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching help requests:', error);
      throw error;
    }
  }

  async createHelpRequest(data: CreateHelpRequestData): Promise<HelpRequest> {
    try {
      const token = await AsyncStorage.getItem('userToken');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.post(
        `${API_URL}/help/requests`,
        data,
        {
          headers: { 'x-auth-token': token }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error creating help request:', error);
      throw error;
    }
  }

  async updateHelpRequestStatus(requestId: string, status: 'open' | 'resolved'): Promise<HelpRequest> {
    try {
      const token = await AsyncStorage.getItem('userToken');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.patch(
        `${API_URL}/help/requests/${requestId}`,
        { status },
        {
          headers: { 'x-auth-token': token }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error updating help request status:', error);
      throw error;
    }
  }

  async deleteHelpRequest(id: string): Promise<void> {
    try {
      const token = await AsyncStorage.getItem('userToken');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.delete(`${API_URL}/help/requests/${id}`, {
        headers: {
          'x-auth-token': token
        }
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Error deleting help request:', error);
      throw error;
    }
  }

  /**
   * Add a response to a help request
   * @param requestId The ID of the help request
   * @param content The content of the response
   * @returns The updated help request with the new response
   */
  async addResponse(requestId: string, content: string): Promise<Response> {
    try {
      const token = await AsyncStorage.getItem('userToken');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await axios.post(
        `${API_URL}/help/requests/${requestId}/responses`,
        { content },
        {
          headers: {
            'x-auth-token': token,
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error adding response:', error);
      throw error;
    }
  }

  /**
   * Get all responses for a help request
   * @param requestId The ID of the help request
   * @returns Array of responses
   */
  async getResponses(requestId: string): Promise<Response[]> {
    try {
      const token = await AsyncStorage.getItem('userToken');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await axios.get(
        `${API_URL}/help/requests/${requestId}/responses`,
        {
          headers: {
            'x-auth-token': token
          }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching responses:', error);
      throw error;
    }
  }

  /**
   * Get all help requests that the current user has responded to
   * @returns Array of help requests with user's responses
   */
  async getUserResponses(): Promise<HelpRequest[]> {
    try {
      const token = await AsyncStorage.getItem('userToken');
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Get all responses by the current user
      const response = await axios.get(
        `${API_URL}/help/responses/user`,
        {
          headers: {
            'x-auth-token': token
          }
        }
      );

      // Get the help request IDs from the responses
      const responses = response.data;
      
      if (!responses || responses.length === 0) {
        console.log('No responses found for user');
        return [];
      }
      
      // Extract unique help request IDs
      const helpRequestIds = responses.map((resp: Response) => resp.helpRequest);
      
      // Remove duplicates and ensure type safety
      const uniqueIds: string[] = Array.from(new Set(helpRequestIds));
      
      if (uniqueIds.length === 0) {
        console.log('No unique help request IDs found');
        return [];
      }
      
      // Fetch the full help request details for each ID
      const helpRequestsPromises = uniqueIds.map(async (id) => {
        try {
          const fullResponse = await axios.get(
            `${API_URL}/help/requests/${id}`,
            {
              headers: {
                'x-auth-token': token
              }
            }
          );
          
          return fullResponse.data;
        } catch (error: any) {
          console.warn(`Help request ${id} not found or error fetching it:`, error.message);
          return null;
        }
      });
      
      const helpRequestsResponses = await Promise.all(helpRequestsPromises);
      
      // Filter out null responses (failed requests)
      const helpRequests = helpRequestsResponses.filter(req => req !== null) as HelpRequest[];
      
      return helpRequests;
    } catch (error) {
      console.error('Error fetching user responses:', error);
      throw error;
    }
  }

  // async generateTitle(description: string): Promise<string> {
  //   try {
  //     const token = await AsyncStorage.getItem('userToken');
  //     const response = await axios.post(
  //       `${API_URL}/help/generate-title`,
  //       { description },
  //       {
  //         headers: { 'x-auth-token': token }
  //       }
  //     );
  //     return response.data.title;
  //   } catch (error) {
  //     console.error('Error generating title:', error);
  //     throw error;
  //   }
  // }

  async deleteResponse(responseId: string): Promise<void> {
    try {
      const token = await AsyncStorage.getItem('userToken');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await axios.delete(`${API_URL}/help/responses/${responseId}`, {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        }
      });

      if (response.status !== 200) {
        throw new Error(`Failed to delete response: ${response.status}`);
      }
    } catch (error: any) {
      console.error('Error deleting response:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: error.config,
        url: error.config?.url
      });
      throw error;
    }
  }

  async getUserResponsesRaw(): Promise<Response[]> {
    try {
      const token = await AsyncStorage.getItem('userToken');
      if (!token) {
        throw new Error('Not authenticated');
      }
      const response = await axios.get(
        `${API_URL}/help/responses/user`,
        {
          headers: {
            'x-auth-token': token
          }
        }
      );
      return response.data as Response[];
    } catch (error) {
      console.error('Error fetching user responses:', error);
      throw error;
    }
  }
}

export default new HelpService(); 